import { useState } from "react";
import { Link, Redirect, useLocation } from "wouter";
import { useQueryClient } from "@tanstack/react-query";
import {
  useStartPhoneChange,
  useVerifyPhoneChangeOld,
  useVerifyPhoneChangeNew,
  useCancelPhoneChange,
} from "@orbit/api-client-react";
import { useAuth } from "../hooks/useAuth";
import { Wordmark } from "../components/Brand";
import { OtpVerificationStep } from "../components/OtpVerificationStep";

type Step =
  | { kind: "enter-new" }
  | {
      kind: "verify-old";
      changeRequestId: string;
      maskedOld: string;
      maskedNew: string;
      expiresAt: string;
    }
  | {
      kind: "verify-new";
      changeRequestId: string;
      maskedNew: string;
      expiresAt: string;
    }
  | { kind: "done"; newPhone: string };

/**
 * /profile/phone-change — three-step wizard.
 *
 *   1. Enter the new phone (server creates a change request and sends
 *      OTP-1 to the OLD phone).
 *   2. Enter OTP-1 (server validates ownership of the old number and
 *      sends OTP-2 to the NEW phone).
 *   3. Enter OTP-2 (server flips customers.phone, marks
 *      phoneVerified=true, writes 'phone_changed' consent log).
 *
 * Why two OTPs (D31): proves continuous control of the old number AND
 * the new — defeats SIM-swap attacks where the attacker has only the
 * new SIM, and lost-phone attacks where the attacker has only the old.
 *
 * Cancel from any step calls /phone-change/cancel which marks the
 * request CANCELLED in the DB so the user can immediately start over
 * with a different new number.
 */
export function ProfilePhoneChangePage() {
  const { isAuthenticated } = useAuth();
  const [, navigate] = useLocation();
  const qc = useQueryClient();

  const [step, setStep] = useState<Step>({ kind: "enter-new" });
  const [newPhone, setNewPhone] = useState("");
  const [error, setError] = useState<string | null>(null);

  const startMut = useStartPhoneChange();
  const verifyOldMut = useVerifyPhoneChangeOld();
  const verifyNewMut = useVerifyPhoneChangeNew();
  const cancelMut = useCancelPhoneChange();

  if (!isAuthenticated) return <Redirect to="/" />;

  async function onStart(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      const r = await startMut.mutateAsync({ newPhone: newPhone.trim() });
      setStep({
        kind: "verify-old",
        changeRequestId: r.changeRequestId,
        maskedOld: r.maskedOldPhone,
        maskedNew: r.maskedNewPhone,
        expiresAt: r.expiresAt,
      });
    } catch (err) {
      const e = err as { status?: number; apiError?: { code?: string; message?: string } };
      if (e.apiError?.code === "PHONE_ALREADY_REGISTERED") {
        setError("That number is already on another account.");
      } else if (e.apiError?.code === "SAME_PHONE") {
        setError("That's already your current number.");
      } else if (e.apiError?.code === "CHANGE_REQUEST_PENDING") {
        setError("You already have a pending phone change. Cancel it first.");
      } else {
        setError(e.apiError?.message ?? "Couldn't start the change. Try again.");
      }
    }
  }

  async function cancel() {
    if (step.kind === "enter-new" || step.kind === "done") {
      navigate("/profile");
      return;
    }
    try {
      await cancelMut.mutateAsync({ changeRequestId: step.changeRequestId });
    } catch {
      // Best-effort cancel — fall through to redirect.
    }
    navigate("/profile");
  }

  return (
    <div className="min-h-screen pb-16">
      <div className="mx-auto max-w-md px-6 pt-8">
        <header className="flex items-center justify-between">
          <Wordmark size="sm" />
          <Link href="/profile" className="text-xs text-ink-dark underline">
            Back
          </Link>
        </header>
        <h1 className="mt-6 text-xl font-bold text-navy">Change your phone</h1>
        <p className="mt-2 text-sm text-ink-dark">
          To keep your account safe, we'll text codes to both numbers.
        </p>

        {step.kind === "enter-new" && (
          <form onSubmit={onStart} className="mt-6 space-y-3">
            <label className="block">
              <span className="text-xs uppercase tracking-wider text-ink-dark/70">
                New phone number
              </span>
              <input
                className="input mt-1"
                value={newPhone}
                onChange={(e) => setNewPhone(e.target.value)}
                placeholder="+639..."
                inputMode="tel"
                autoComplete="tel"
                required
              />
            </label>
            {error && <p className="text-red-600 text-sm">{error}</p>}
            <button
              type="submit"
              className="btn-primary w-full"
              disabled={startMut.isPending}
            >
              {startMut.isPending ? "Sending code…" : "Send code"}
            </button>
            <button
              type="button"
              onClick={() => navigate("/profile")}
              className="text-xs text-ink-dark underline w-full text-center"
            >
              Cancel
            </button>
          </form>
        )}

        {step.kind === "verify-old" && (
          <div className="mt-6 card">
            <OtpVerificationStep
              title="Step 1 of 2 — verify your current number"
              body={
                <>
                  We sent a 6-digit code to{" "}
                  <span className="font-medium">{step.maskedOld}</span>.
                </>
              }
              expiresAt={step.expiresAt}
              busy={verifyOldMut.isPending}
              secondaryAction={{ label: "Cancel change", onClick: cancel }}
              onSubmit={async (code) => {
                const r = await verifyOldMut.mutateAsync({
                  changeRequestId: step.changeRequestId,
                  code,
                });
                setStep({
                  kind: "verify-new",
                  changeRequestId: r.changeRequestId,
                  maskedNew: r.maskedNewPhone,
                  expiresAt: r.expiresAt,
                });
              }}
            />
          </div>
        )}

        {step.kind === "verify-new" && (
          <div className="mt-6 card">
            <OtpVerificationStep
              title="Step 2 of 2 — verify your new number"
              body={
                <>
                  We sent a 6-digit code to{" "}
                  <span className="font-medium">{step.maskedNew}</span>.
                </>
              }
              expiresAt={step.expiresAt}
              busy={verifyNewMut.isPending}
              secondaryAction={{ label: "Cancel change", onClick: cancel }}
              onSubmit={async (code) => {
                const r = await verifyNewMut.mutateAsync({
                  changeRequestId: step.changeRequestId,
                  code,
                });
                await qc.invalidateQueries({ queryKey: ["customer-me"] });
                await qc.invalidateQueries({ queryKey: ["customer-profile"] });
                setStep({ kind: "done", newPhone: r.newPhone });
              }}
            />
          </div>
        )}

        {step.kind === "done" && (
          <div className="mt-6 card border-green-200 bg-green-50">
            <h2 className="text-base font-semibold text-green-900">
              Phone updated
            </h2>
            <p className="mt-2 text-sm text-green-900">
              Your new number {step.newPhone} is now on file and verified.
            </p>
            <Link
              href="/profile"
              className="mt-4 inline-block text-sm font-medium underline text-green-900"
            >
              Back to profile →
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
